import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Authenticator from "components/Authenticator";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import handlePDFDownload from "components/Calculators/components/SaveSharePDF/components/handlePDFDownload";
import useMediaQuery from "@mui/material/useMediaQuery";
import saveOrUpdate from "components/Calculators/components/utils/saveOrUpdate";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

const SaveSharePDF = ({
  answers,
  allowedQuestionTitles, // this is to control which questions are visible by title
  generateAnswerOptions,
  calc,
  setCalc,
  calculatorType,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    showLogInDialog,
    setShowLogInDialog,
    user,
    userProfile,
    snackbarData,
    setSnackbarData,
  } = useStore(AppStore);
  const [showShareFields, setShowShareFields] = useState(false);
  // const [showLogInDialog, setShowLogInDialog] = useState(false);
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);

  const calcType = calculatorType;

  return (
    <Alert
      sx={{
        margin: theme.spacing(2, 0, 0, 0),
        width: "100%",
        overflow: "hidden",
        "& .MuiAlert-message": {
          width: "100%",
        },
      }}
      severity={"info"}
      variant="outlined"
    >
      <>
        <Box sx={{ fontSize: theme.spacing(3), fontWeight: 600 }}>
          Get PDF, save or share?
        </Box>

        <Typography
          component="h2"
          sx={{
            display: "flex",
            fontSize: theme.spacing(1.5),
            margin: theme.spacing(0, 0, 0, "auto"),
            [theme.breakpoints.down("md")]: {
              display: "block",
            },
          }}
        >
          *All your calculations will be stored in{" "}
          <Link
            sx={{
              margin: theme.spacing(0, 0, 0, 0.5),
              color: theme.palette.highContrastLink.main,
            }}
            rel="noopener"
            target="_blank"
            href="/my/calculations"
          >
            {" "}
            My Calculations
          </Link>
        </Typography>
      </>

      {userProfile && Object.keys(userProfile).length === 0 && (
        <Box
          sx={{
            margin: theme.spacing(2, 0, 0, 0),
            display: "flex",
            flexDirection: "row",
            gap: theme.spacing(2),
            justifyContent: "space-between",
            position: "relative",
            height: theme.spacing(8),
            transformStyle: "preserve-3d",
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
              height: "auto",
              gap: theme.spacing(2.5),
              alignItems: "center",
            },
          }}
        >
          <Button
            onClick={() => {
              setShowLogInDialog(!showLogInDialog);
            }}
            color="info"
            variant={"contained"}
            sx={{
              flex: 1,
              fontWeight: 700,
              boxShadow: 2,
              transform: "none",
              height: theme.spacing(6.5),
              transformOrigin: "bottom center",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                transform: "none",
                boxShadow: 3,
                zIndex: 2,
                filter: "brightness(1.05)",
                borderWidth: "2px",
              },
              [theme.breakpoints.down("md")]: {
                width: "66%",
                margin: theme.spacing(3, "auto", 0, "auto"),
                py: 1.5,
              },
              [theme.breakpoints.down("sm")]: {
                minHeight: theme.spacing(7),
              },
            }}
          >
            {showLogInDialog ? (
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  fontWeight: 700,
                }}
              >
                Cancel
              </Box>
            ) : (
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <PictureAsPdfIcon
                  sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                />
                PDF
              </Box>
            )}
          </Button>
          <Button
            color={"primary"}
            variant={"outlined"}
            sx={{
              flex: 1,
              fontWeight: 700,
              boxShadow: 1,
              transform: "none",
              height: theme.spacing(6.5),
              transformOrigin: "bottom center",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                transform: "none !important",
                boxShadow: 1,
                zIndex: 1,
                filter: "brightness(1.05)",
                borderWidth: "1px !important",
              },
              [theme.breakpoints.down("md")]: {
                width: "66%",
                margin: theme.spacing(3, "auto", 0, "auto"),
                py: 1.5,
              },
              [theme.breakpoints.down("sm")]: {
                minHeight: theme.spacing(7),
              },
            }}
            onClick={() => {
              setShowLogInDialog(!showLogInDialog);
            }}
          >
            {showLogInDialog ? (
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  fontWeight: 700,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Cancel
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: theme.spacing(0.8),
                    fontWeight: 700,
                  }}
                >
                  <ShareOutlinedIcon
                    sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                  />
                  Share
                </Box>
              </Box>
            )}
          </Button>
          <Button
            color="success"
            variant={"contained"}
            sx={{
              flex: 1,
              fontWeight: 700,
              boxShadow: 2,
              transform: "none",
              height: theme.spacing(6.5),
              transformOrigin: "bottom center",
              transition: "box-shadow 0.3s ease",
              "&:hover": {
                transform: "none",
                boxShadow: 3,
                zIndex: 2,
                filter: "brightness(1.05)",
                borderWidth: "2px",
              },
              [theme.breakpoints.down("md")]: {
                width: "66%",
                margin: theme.spacing(3, "auto", 0, "auto"),
                py: 1.5,
              },
              [theme.breakpoints.down("sm")]: {
                minHeight: theme.spacing(7),
              },
            }}
            onClick={() => {
              setShowLogInDialog(!showLogInDialog);
            }}
          >
            {showLogInDialog ? (
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  fontWeight: 700,
                }}
              >
                Cancel
              </Box>
            ) : (
              <Box
                sx={{
                  padding: theme.spacing(0.8),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <SaveIcon
                  sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                />
                Save
              </Box>
            )}
          </Button>
        </Box>
      )}

      <Dialog
        open={
          showLogInDialog &&
          userProfile &&
          Object.keys(userProfile).length !== 0
        }
        onClose={() => setShowLogInDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Authenticator customTitle={"Log in or sign up to do that"} />
        </DialogContent>
      </Dialog>

      <Collapse
        in={
          showLogInDialog ||
          (userProfile && Object.keys(userProfile).length !== 0)
        }
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            margin: theme.spacing(3, "auto", 0, "auto"),
            width: "100%",
          }}
        >
          {/* {userProfile && Object.keys(userProfile).length === 0 && (
            <Box
              sx={{
                margin: theme.spacing(3, "7.5%", 5, "7.5%"),
                width: "auto",
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                },
              }}
            >
              <Authenticator
                customTitle={"Please log in or sign up to do that"}
              />
            </Box>
          )} */}
          {userProfile && Object.keys(userProfile).length > 0 && (
            <Box>
              {answers
                .filter((answer) => {
                  // filtering out the question that we don't want to show in this part of the UI
                  return allowedQuestionTitles.includes(answer.q_title);
                })
                .map((answer, index) => {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        padding: theme.spacing(1),
                        borderTop: `none`,
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                        "& .MuiFormControl-root": {
                          margin: theme.spacing(1, 0, 0, "auto"),
                          display: "flex",
                          width: "100%",
                        },
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="h2"
                          sx={{ fontSize: theme.spacing(3) }}
                        >
                          {answer.q_title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <Box
                          sx={{
                            flex: "0 1 50%",
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              margin: theme.spacing("auto", 0),
                              fontSize: theme.spacing(1.8),
                            }}
                          >
                            {answer?.q_description &&
                              answer?.q_description(answers)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            displat: "flex",
                            flex: "1 1 50%",
                          }}
                        >
                          {generateAnswerOptions(answer, index, answers)}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: theme.spacing(1),
                  "& .MuiFormControl-root": {
                    margin: theme.spacing(1, 0, 0, "auto"),
                    display: "flex",
                    width: "100%",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: theme.spacing(2),
                    justifyContent: "space-between",
                    padding: theme.spacing(1, 0),
                    position: "relative",
                    height: theme.spacing(8),
                    transformStyle: "preserve-3d",
                    [theme.breakpoints.down("sm")]: {
                      flexDirection: "column",
                      height: "auto",
                      gap: theme.spacing(2.5),
                      padding: theme.spacing(2, 0),
                    },
                  }}
                >
                  <Button
                    onClick={() => {
                      const isPDFGenerated = true;
                      saveOrUpdate({
                        user,
                        userProfile,
                        answers,
                        calc,
                        calcType,
                        setCalc,
                        showUpdateFields,
                        setShowCreatedSuccessMessage,
                        setSnackbarData,
                        isPDFGenerated,
                      });
                      handlePDFDownload(
                        setSnackbarData,
                        theme,
                        isMobile,
                        calcType
                      );
                    }}
                    color="info"
                    variant={"contained"}
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      boxShadow: 2,
                      transform: "none",
                      height: theme.spacing(6),
                      transformOrigin: "bottom center",
                      transition: "box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "none",
                        boxShadow: 4,
                        zIndex: 2,
                        filter: "brightness(1.05)",
                        borderWidth: "2px",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                        py: 1.5,
                      },
                      [theme.breakpoints.down("sm")]: {
                        minHeight: theme.spacing(7),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        padding: theme.spacing(0.8),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {" "}
                      <PictureAsPdfIcon
                        sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                      />{" "}
                      PDF
                    </Box>
                  </Button>
                  <Button
                    color={"primary"}
                    variant={"outlined"}
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      display: "flex",
                      textDecoration: "none !important",
                      borderWidth: 1,
                      boxShadow: 1,
                      transform: "none",
                      height: theme.spacing(6),
                      transformOrigin: "bottom center",
                      transition: "box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "none",
                        boxShadow: 2,
                        zIndex: 2,
                        filter: "brightness(1.05)",
                        borderWidth: "2px",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                        py: 1.5,
                      },
                      [theme.breakpoints.down("sm")]: {
                        minHeight: theme.spacing(7),
                      },
                    }}
                    onClick={async () => {
                      const calc = await saveOrUpdate({
                        user,
                        userProfile,
                        answers,
                        calc,
                        calcType,
                        setCalc,
                        showUpdateFields,
                        setShowCreatedSuccessMessage,
                        setSnackbarData: false,
                      });

                      navigator.clipboard
                        .writeText(
                          `${
                            process.env.deployment === "dev"
                              ? "http://localhost:3000"
                              : "https://www.offermarket.us"
                          }/my/calculations?ref=${calc?.RefTag}`
                        )
                        .then(() => {
                          setShowCopiedSuccess(true);

                          setTimeout(function () {
                            setShowCopiedSuccess(false);
                          }, 10000);
                        });

                      setSnackbarData({
                        message: "Share link copied to clipboard",
                        options: {
                          variant: "success",
                          autoHideDuration: 5000,
                          anchorOrigin: {
                            vertical: "top",
                            horizontal: "center",
                          },
                        },
                      });
                    }}
                  >
                    {showCopiedSuccess ? (
                      <Box
                        sx={{
                          padding: theme.spacing(0.8),
                          fontWeight: 700,
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <>
                          <ContentCopyIcon size="lg" />
                          <Box
                            sx={{
                              margin: theme.spacing(1),
                            }}
                          >
                            Link copied
                          </Box>
                        </>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: theme.spacing(0.8),
                            fontWeight: 700,
                          }}
                        >
                          {" "}
                          <ShareOutlinedIcon
                            sx={{ fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                          />{" "}
                          <Box
                            sx={{
                              margin: theme.spacing(0, 1),
                            }}
                          >
                            Share
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Button>

                  <Button
                    onClick={() => {
                      saveOrUpdate({
                        user,
                        userProfile,
                        answers,
                        calc,
                        calcType,
                        setCalc,
                        showUpdateFields,
                        setShowCreatedSuccessMessage,
                        setSnackbarData,
                      });
                    }}
                    color="success"
                    variant={"contained"}
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      boxShadow: 2,
                      transform: "none",
                      height: theme.spacing(6),
                      transformOrigin: "bottom center",
                      transition: "box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "none",
                        boxShadow: 3,
                        zIndex: 2,
                        filter: "brightness(1.05)",
                        borderWidth: "2px",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                        py: 1.5,
                      },
                      [theme.breakpoints.down("sm")]: {
                        minHeight: theme.spacing(7),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        padding: theme.spacing(0.8),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {" "}
                      <SaveIcon
                        sx={{ mr: 1, fontSize: { xs: "1.4rem", sm: "1.2rem" } }}
                      />
                      {!calc ? "Save" : "Update"}
                    </Box>
                  </Button>
                </Box>
                {showCreatedSuccessMessage &&
                  (snackbarData.message.includes("copied") ? (
                    <Box>
                      <Alert
                        variant="outlined"
                        icon={
                          <Box>
                            <ShareOutlinedIcon size="lg" />
                          </Box>
                        }
                        sx={{
                          width: "100% !important",
                          "& .MuiAlert-message": {
                            width: "100% !important",
                          },
                          display: "flex",
                          margin: theme.spacing(0, 0, 1, 0),
                          color: "black !important",
                        }}
                        color={"info"}
                      >
                        <Box
                          sx={{
                            textAlign: "left",
                            fontSize: theme.spacing(3),
                            color: theme.palette.info.main,
                            display: "flex",
                          }}
                        >
                          Share your calculation{" "}
                        </Box>
                        <Box>
                          <TextField
                            sx={{
                              textAlign: "left",
                              width: "100%",
                              margin: theme.spacing(2, 0),
                            }}
                            label={
                              calc?.RefTag
                                ? "Share URL"
                                : "Press SAVE to generate a link"
                            }
                            variant="outlined"
                            value={
                              calc?.RefTag
                                ? `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                : ""
                            }
                            InputLabelProps={{
                              shrink: calc?.RefTag
                                ? `${
                                    process.env.deployment === "dev"
                                      ? "http://localhost:3000"
                                      : "https://www.offermarket.us"
                                  }/my/calculations?ref=${calc?.RefTag}`
                                : "",
                            }}
                          />
                          <Box>
                            A business partner with your link will be able to
                            view and edit your calculation
                          </Box>
                        </Box>
                      </Alert>
                    </Box>
                  ) : (
                    <Alert variant="outlined" sx={{ margin: theme.spacing(1) }}>
                      Your calculation has been saved!
                      <br />
                      <br />
                      To update or compare your calculations, go to{" "}
                      <a href="/my/calculations">Calculations</a>{" "}
                    </Alert>
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Alert>
  );
};

export default SaveSharePDF;
